import { Stack } from '@mui/material'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import CustomChip from '@/components/dataDisplay/CustomChip'
import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { getControlTypeLabel } from '@/features/resource/utils/controlType'
import type { GroupedPort } from '@/features/resource/utils/portGroups'

export type PortTypeDescriptor = {
  /** The categorical part, which goes in the chip. Kept short so a rare state cannot size the column. */
  label: string
  /** A caveat about that category, shown beside the chip rather than inside it. */
  qualifier: string | null
}

/**
 * How a port's type reads: a category, plus a caveat when part of it is unknown.
 *
 * Split in two because they are different things. Folding "type unknown" into the chip made a rare
 * state the longest string in the column — 26 characters against "Metering"'s 8 — and sizing the
 * column for it would have starved Detail, which carries the longest free text on the row.
 *
 * Interpolated rather than concatenated because word order varies by language. `category` is
 * nullable and `getControlTypeLabel` returns an empty string for null; `isOutput` is nullable at
 * both database layers. Either can be missing, so both have a caveat.
 */
export function getPortTypeDescriptor(port: GroupedPort, t: TFunction<'translation'>): PortTypeDescriptor {
  if (port.kind === 'METERING') {
    return { label: t('resources.detail_panel.port_type_metering'), qualifier: null }
  }

  const signalType = getControlTypeLabel(port.controlPort.category, t)
  const hasSignalType = signalType !== ''
  const direction = port.controlPort.isOutput ?? null

  if (direction === null) {
    return hasSignalType
      ? { label: signalType, qualifier: t('resources.detail_panel.port_type_qualifier_direction_unknown') }
      : { label: t('resources.detail_panel.port_type_unknown'), qualifier: null }
  }

  const directionOnly = direction
    ? t('resources.detail_panel.port_type_output_only')
    : t('resources.detail_panel.port_type_input_only')

  if (!hasSignalType) {
    return { label: directionOnly, qualifier: t('resources.detail_panel.port_type_qualifier_type_unknown') }
  }

  return {
    label: direction
      ? t('resources.detail_panel.port_type_output', { type: signalType })
      : t('resources.detail_panel.port_type_input', { type: signalType }),
    qualifier: null,
  }
}

/**
 * The hues for the port-type chips, defined here rather than taken from the theme palette.
 *
 * These encode data, not component state. A palette is sized for component semantics — primary,
 * success, warning — and a data domain can carry more values than the palette has names, so do not
 * fold these back into it. They still have to stay clear of meaning that *is* semantic: the metering
 * hue is a dark amber-brown rather than an orange, so it reads as a category beside the Errors
 * column rather than as a warning.
 */
const PORT_TYPE_HUES = {
  input: '#5a4fbf',
  output: '#0f7f95',
  metering: '#96591a',
} as const

/**
 * The chip's hue, or null for the neutral chip.
 *
 * An unknown direction gets no hue: "we do not know" should read as the absence of a category, not
 * as one more of them.
 */
export function getPortTypeHue(port: GroupedPort): string | null {
  if (port.kind === 'METERING') {
    return PORT_TYPE_HUES.metering
  }

  if ((port.controlPort.isOutput ?? null) === null) {
    return null
  }

  return port.controlPort.isOutput ? PORT_TYPE_HUES.output : PORT_TYPE_HUES.input
}

/**
 * The port's type, as a chip.
 *
 * The colour is redundant reinforcement rather than the signal: the label still spells the type out,
 * so nothing is lost to anyone who cannot separate the hues.
 */
export default function PortTypeChip({ port }: Readonly<{ port: GroupedPort }>) {
  const { t } = useTranslation()

  const { label, qualifier } = getPortTypeDescriptor(port, t)
  const hue = getPortTypeHue(port)

  return (
    <Stack alignItems="flex-start" gap={0.25}>
      <CustomChip
        // No `color` when a hue is set: CustomChip spreads its own styles *after* sx, and its
        // `outlined` + `default` branch would overwrite the hue with text.secondary. Omitting the
        // prop skips that branch; the neutral chip still asks for it deliberately.
        color={hue === null ? 'default' : undefined}
        label={label}
        size="small"
        sx={{
          // The hue is both border and text on an outlined chip, as in the mockup.
          ...(hue !== null && { borderColor: 'currentColor', color: hue }),
          // Wrapping rather than the Chip's default ellipsis: the column is sized so this never
          // triggers, but a longer translation should cost a line rather than the end of a word.
          height: 'auto',
          '& .MuiChip-label': { overflow: 'visible', paddingY: 0.25, whiteSpace: 'normal' },
        }}
        variant="outlined"
      />
      {qualifier !== null && (
        <CustomTypography color="grey.500" variant="caption">
          {qualifier}
        </CustomTypography>
      )}
    </Stack>
  )
}
